import { Check, ChevronDown, Lock } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { SEARCH_TEXTS } from '@till-erp/platform/lists';
import { comboKey, createOffer, filterOptions, initialActive, labelSegments, withSimilarFirst, type ComboMatch, type ComboOption, type CreateOffer } from '../../lib/combobox.ts';
import { useScreen } from '../../lib/layout.ts';
import { focusables } from '../../lib/modal-focus.ts';
import { cn } from '../../lib/utils.ts';
import { useUndo } from '../undo-toast.tsx';
import { joinIds, lockText, type FieldLock } from './input.tsx';
import { Popover } from './popover.tsx';
import { fieldClass } from './variants.ts';
import { pillToneClass } from './pill-tone.ts';
import { Icon } from './icon.tsx';

export type { ComboOption } from '../../lib/combobox.ts';

/** Feste Texte der Auswahl mit Suche (Oberfläche Deutsch). */
export const COMBOBOX_TEXTS = {
  placeholder: 'Bitte wählen',
  search: 'Suchen …',
  nothing: 'Keine Treffer.',
  loading: 'Lädt …',
  loadError: 'Liste konnte nicht geladen werden.',
  more: 'Weitere Treffer – genauer suchen.',
  typeToSearch: 'Nummer oder Name eingeben.',
  create: (text: string) => `+ „${text}“ neu anlegen`,
  /** Ticket 5.90: Frage, wenn der Text einem vorhandenen Eintrag ähnelt (Entwurf Variante A). */
  similar: (label: string) => `Meinst du „${label}“?`,
  createAnywayRow: (text: string) => `„${text}“ trotzdem neu anlegen`,
  /** Ticket 5.90 (Sichtung): Frage als Untertitel am vorhandenen Eintrag – eine Zeile statt Frage und Zeile. */
  existing: 'Meinst du diesen? Schon vorhanden – nehmen ↵',
  /** Marke an neu angelegten (noch nicht gespeicherten) Einträgen. */
  newMark: 'neu',
  saveError: 'Konnte nicht gespeichert werden. Bitte noch einmal versuchen.',
} as const;

/** Zustand einer Suche beim Server (Kunde, Artikel, Lieferant): Optionen kommen schon gefiltert. */
export interface ComboRemote {
  /** Suchtext geändert (die Hülle fragt den Server, verzögert). */
  onQuery: (query: string) => void;
  /** Mindestlänge laut Antwort `too_short`; `null` = lang genug. */
  tooShort?: number | null;
  /** Es gibt weitere Treffer (kein Blättern, genauer suchen). */
  more?: boolean;
}

/** Breite nach Inhalt (DESIGN: Land, Währung, Lagerplatz `field-s`–`field-m`; Kunde, Artikel, Lieferant `field-l`). */
export type ComboWidth = 's' | 'm' | 'l' | 'full';

const WIDTH: Record<ComboWidth, string> = { s: 'w-field-s', m: 'w-field-m', l: 'w-field-l', full: 'w-full' };

export interface ComboboxProps {
  id?: string;
  /** Technischer Wert (ISO-Kürzel, id) oder `null`. */
  value: string | null;
  /**
   * Neuer Wert nach Auswahl (Enter, Tab, Klick, Tippen am Handy). `null` nur beim Sofort-Speichern: Rückgängig oder
   * Fehler stellen einen leeren alten Wert wieder her.
   */
  onChange: (value: string | null, option: ComboOption | null) => void;
  options: readonly ComboOption[];
  /** Name der Liste für Hilfsmittel (am Handy Überschrift des Blatts), z. B. „Land“. */
  label: string;
  /** Häufige Werte oben über dem feinen Strich. */
  frequent?: readonly string[];
  /** Suche beim Server statt in den festen Optionen. */
  remote?: ComboRemote;
  /** Anzeige des Werts, wenn er (noch) nicht unter den Optionen steht (z. B. vorbelegter Kunde). */
  selectedLabel?: string | null;
  loading?: boolean;
  /** Liste konnte nicht geladen werden (Endpunkt 4xx/5xx); das Feld bleibt bedienbar. */
  loadError?: boolean;
  width?: ComboWidth;
  invalid?: boolean;
  disabled?: boolean;
  /** Gesperrt (DESIGN „Gesperrtes Feld“): ohne Fläche, Schloss statt Chevron, Hilfsmittel lesen „nicht änderbar“ (+ Grund). */
  locked?: FieldLock;
  placeholder?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
  /**
   * „+ „<Text>“ neu anlegen“ als letzte Zeile, wenn kein Name genau passt; liefert die neue Option (wird gewählt).
   * `confirmSimilar` (Ticket 5.90): „trotzdem neu“ nach der Frage „Meinst du …?“.
   */
  onCreate?: (text: string, confirmSimilar: boolean) => Promise<ComboOption>;
  /** Text der Zeile „neu anlegen“ (Ticket 5.90), z. B. „+ „Kühlung“ als neue Eigenschaft anlegen“. */
  createLabel?: (text: string) => string;
  /** Vorhandener Eintrag, dem der Text ähnelt (eine Regel mit dem Server, z. B. `similarOption`); dann erst die Frage. */
  similarOf?: (text: string) => ComboOption | null;
  /** Gewählter Wert ist neu (noch nicht angelegt): Pille „neu“ im Auslöser (Ticket 5.90). */
  isNew?: boolean;
  /**
   * Sofort speichern (z. B. Lagerplatz): wird beim Auswählen gerufen, danach „Rückgängig“ 10 s; Rückgängig ruft
   * `onCommit` mit dem alten Wert und `{ undo: true }`. Fehler → Satz unter dem Feld, Anzeige wieder auf dem alten Wert.
   */
  onCommit?: (value: string | null, meta?: { undo: boolean }) => Promise<void>;
  /** Satz der Rückgängig-Einblendung, z. B. „Lagerplatz A-01 → B-02 gewechselt“. */
  commitText?: (from: string | null, to: string) => string;
  testId?: string;
  /**
   * Darstellung (Ticket 1.262): `field` (Standard) im Feld-Look 44 px; `cell` in einer Werte-Liste – im Ruhezustand wie
   * eine Zelle (Text ohne Rahmen, 32 px, grober Zeiger 44 px, kleines Chevron), Feld-Look nur bei Fehler. Die Liste ist
   * dann mindestens so breit wie `field-m`.
   */
  appearance?: 'field' | 'cell';
  /** Kennung der Zelle (`data-cell` am Auslöser), damit die Werte-Liste den Fokus weitergeben kann. */
  cellKey?: string;
}

/** Mindestbreite der Liste einer Zell-Auswahl: `field-m` (240 px) abzüglich Innenabstand des Popovers (2 × 8 px). */
const CELL_PANEL_MIN = 240 - 16;

type Row = { kind: 'option'; match: ComboMatch } | { kind: 'create'; text: string; confirm: boolean };

const NO_OFFER: CreateOffer = { kind: 'none' };

/** Fehlertext einer abgelehnten Aktion. */
function errorText(err: unknown): string {
  return err instanceof Error && err.message.trim() !== '' ? err.message : COMBOBOX_TEXTS.saveError;
}

/**
 * Auswahl mit Suche (Baustein B, Ticket 1.257; DESIGN „Auswahl mit Suche (`Combobox`)“, EXPERIENCE §4.3): ein
 * Baustein für alle Auswahlen mit mehr als etwa 10 Einträgen. Auslöser ist ein Knopf im Feld-Look (`fieldClass`,
 * Chevron rechts) – nie ein freies Textfeld, damit nie ein getipptes Kürzel gespeichert wird. Offen: Suchzeile
 * `role="combobox"` über einer Liste `role="listbox"` (höchstens 320 px hoch), Filter mit Faltung (Groß/klein und
 * Umlaute egal), Treffer halbfett, häufige Werte oben über feinem Strich. Tippen auf dem geschlossenen Feld öffnet und
 * übernimmt den Buchstaben. ↑/↓ markieren, Enter übernimmt, Tab übernimmt und geht weiter, Esc schließt ohne Änderung;
 * der Fokus kehrt aufs Feld zurück. Am Handy (unter 768 px) als Blatt von unten (über `Popover`).
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  label,
  frequent = [],
  remote,
  selectedLabel,
  loading = false,
  loadError = false,
  width = 'm',
  invalid = false,
  disabled = false,
  locked,
  placeholder = COMBOBOX_TEXTS.placeholder,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  className,
  onCreate,
  createLabel = COMBOBOX_TEXTS.create,
  similarOf,
  isNew = false,
  onCommit,
  commitText,
  testId,
  appearance = 'field',
  cellKey,
}: ComboboxProps) {
  const ownId = useId();
  const triggerId = id ?? `${ownId}-feld`;
  const listId = `${ownId}-liste`;
  const statusId = `${ownId}-stand`;
  const errorId = `${ownId}-fehler`;
  const valueId = `${ownId}-wert`;
  const lockId = `${ownId}-schloss`;
  const phone = useScreen() === 'phone';
  const isLocked = disabled || !!locked;
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Zuletzt gewählte Option: Anzeige auch dann, wenn die Server-Suche sie nicht mehr liefert. */
  const [picked, setPicked] = useState<ComboOption | null>(null);
  /** Breite des Felds beim Öffnen (Liste gleich breit). */
  const [panelWidth, setPanelWidth] = useState<number | undefined>(undefined);
  /** Fokus nach dem Schließen: aufs Feld oder (Tab) weiter. */
  const focusAfter = useRef<'field' | 'next' | 'prev' | null>(null);
  const undo = useUndo((err) => setError(errorText(err)));

  const result = useMemo(() => filterOptions(options, query, frequent, !!remote), [options, query, frequent, remote]);
  const offer = useMemo(() => (onCreate && !loading && !loadError ? createOffer(options, query, similarOf) : NO_OFFER), [onCreate, loading, loadError, options, query, similarOf]);
  const rows: Row[] = useMemo(() => {
    // Ticket 5.90: bei ähnlicher Schreibweise steht der vorhandene Eintrag oben (Enter nimmt ihn), „trotzdem neu“ unten.
    const list: Row[] = withSimilarFirst([...result.frequent, ...result.rest], offer).map((match) => ({ kind: 'option', match }));
    if (offer.kind !== 'none') list.push({ kind: 'create', text: offer.text, confirm: offer.kind === 'similar' });
    return list;
  }, [result, offer]);
  const current = rows.length === 0 ? -1 : Math.min(Math.max(active, 0), rows.length - 1);
  const optionId = (i: number) => `${ownId}-eintrag-${i}`;

  const shown = options.find((o) => o.value === value) ?? (picked && picked.value === value ? picked : null);
  const display = value === null || value === '' ? null : (shown?.label ?? selectedLabel ?? value);

  function openWith(text: string) {
    if (isLocked || busy) return;
    setQuery(text);
    remote?.onQuery(text);
    setError(null);
    const all = filterOptions(options, text, frequent, !!remote);
    setActive(text === '' ? initialActive([...all.frequent, ...all.rest].map((m) => m.option.value), value) : 0);
    // Innenbreite: das Popover hat 8 px Innenabstand je Seite.
    setPanelWidth(trigger.current ? Math.max(trigger.current.offsetWidth - 16, appearance === 'cell' ? CELL_PANEL_MIN : 0) : undefined);
    setOpen(true);
  }

  function close(focus: 'field' | 'next' | 'prev' | null) {
    focusAfter.current = focus;
    setOpen(false);
  }

  // Nach dem Schließen (das Blatt gibt den Fokus erst zurück): Fokus aufs Feld bzw. zum nächsten Element.
  useEffect(() => {
    if (open || focusAfter.current === null) return;
    const where = focusAfter.current;
    focusAfter.current = null;
    const frame = requestAnimationFrame(() => {
      const el = trigger.current;
      if (!el) return;
      if (where === 'next') {
        const root = el.parentElement?.closest<HTMLElement>('[role="dialog"]') ?? document.body;
        const list = focusables(root);
        const next = list[list.indexOf(el) + 1];
        (next ?? el).focus();
      } else if (where === 'prev') {
        const root = el.parentElement?.closest<HTMLElement>('[role="dialog"]') ?? document.body;
        const list = focusables(root);
        const prev = list[list.indexOf(el) - 1];
        (prev ?? el).focus();
      } else el.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // Beim Öffnen Fokus in die Suchzeile, Schreibmarke ans Ende (übernommener Buchstabe).
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      const el = input.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  /** Alten Wert wieder anzeigen (Rückgängig, Fehler beim Sofort-Speichern). */
  function restore(before: string | null) {
    if (before === null) onChange(null, null);
    else onChange(before, options.find((o) => o.value === before) ?? { value: before, label: before });
  }

  async function pick(row: Row | undefined, focus: 'field' | 'next' | 'prev') {
    if (!row || busy) return;
    if (row.kind === 'create') {
      if (!onCreate) return;
      setBusy(true);
      try {
        const created = await onCreate(row.text, row.confirm);
        setPicked(created);
        onChange(created.value, created);
        setError(null);
        close(focus);
      } catch (err) {
        setError(errorText(err));
        close('field');
      } finally {
        setBusy(false);
      }
      return;
    }
    const option = row.match.option;
    close(focus);
    if (option.value === value) return;
    const before = value;
    setPicked(option);
    onChange(option.value, option);
    setError(null);
    if (!onCommit) return;
    // Altes Rückgängig-Angebot weg: es darf nicht neben dem neuen Speichern laufen (sonst entscheidet die Antwortreihenfolge).
    undo.dismiss();
    setBusy(true);
    try {
      await onCommit(option.value);
      undo.offer({
        text: commitText ? commitText(before, option.value) : `${label} geändert`,
        undo: async () => {
          // Während Rückgängig läuft, ist das Feld gesperrt (kein zweites Speichern daneben).
          setBusy(true);
          try {
            await onCommit(before, { undo: true });
            restore(before);
          } finally {
            setBusy(false);
          }
        },
      });
    } catch (err) {
      // Anzeige zurück auf den alten Wert (auch auf „leer“).
      restore(before);
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function onSearchKey(e: KeyboardEvent<HTMLInputElement>) {
    // Enter bestätigt hier noch die Eingabe-Hilfe (Akzente, Kana), nicht die Auswahl.
    if (e.nativeEvent.isComposing) return;
    const next = comboKey({ active: current, count: rows.length }, e.key);
    if (!next.handled) return;
    // Esc gehört der Suchzeile (nicht dem Ebenen-Stapel); Tab bewegt den Fokus erst nach dem Schließen.
    e.preventDefault();
    setActive(next.active);
    const focus = e.key === 'Tab' && e.shiftKey ? 'prev' : next.focus;
    // Tab legt nie etwas an: auf der Zeile „neu anlegen“ nur schließen und weiter.
    if (next.action === 'pick' && e.key === 'Tab' && rows[next.active]?.kind === 'create') close(focus);
    else if (next.action === 'pick') void pick(rows[next.active], focus ?? 'field');
    else if (next.action === 'close') close(focus);
  }

  function onTriggerKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (isLocked) return;
    const printable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
    if (open) {
      // Schnell weitergetippt, bevor die Suchzeile den Fokus hat: Zeichen nicht verlieren.
      if (!printable) return;
      e.preventDefault();
      const text = query + e.key;
      setQuery(text);
      setActive(0);
      remote?.onQuery(text);
      input.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openWith('');
    } else if (printable && e.key !== ' ') {
      // Tippen auf dem geschlossenen Feld öffnet und übernimmt den Buchstaben.
      e.preventDefault();
      openWith(e.key);
    }
  }

  const status: string | null = loadError
    ? COMBOBOX_TEXTS.loadError
    : loading
      ? COMBOBOX_TEXTS.loading
      : remote && query.trim() === '' && options.length === 0
        ? COMBOBOX_TEXTS.typeToSearch
        : remote?.tooShort
          ? SEARCH_TEXTS.tooShort(remote.tooShort)
          : result.frequent.length + result.rest.length === 0
            ? COMBOBOX_TEXTS.nothing
            : null;
  // Zell-Darstellung ohne Fehler: wie Text in der Zeile; mit Fehler im Feld-Look (rot umrandet).
  const cellLook = appearance === 'cell' && !invalid && !error;
  const separatorAt = offer.kind !== 'similar' && result.frequent.length > 0 && result.rest.length > 0 ? result.frequent.length : -1;

  return (
    <div data-combobox="" data-testid={testId} className={cn(WIDTH[width], 'max-w-full max-tablet:w-full', className)}>
      <Popover
        open={open}
        onOpenChange={(next) => (next ? openWith('') : close(null))}
        label={label}
        fixed
        autoFocus={false}
        className="p-space-2"
        trigger={(props) => (
          <button
            ref={trigger}
            id={triggerId}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={props['aria-expanded']}
            aria-controls={props['aria-controls']}
            aria-label={ariaLabel}
            aria-invalid={invalid || !!error || undefined}
            aria-describedby={joinIds(valueId, locked ? lockId : undefined, describedBy, error ? errorId : undefined)}
            aria-busy={busy || undefined}
            disabled={isLocked}
            onClick={props.onClick}
            onKeyDown={onTriggerKey}
            data-field-box={isLocked ? 'locked' : 'edit'}
            data-value={value ?? ''}
            data-cell={cellKey}
            className={
              cellLook
                ? cn(
                    'group relative flex h-8 w-full items-center gap-space-1 rounded-field px-space-2 text-left text-md hover:bg-tile pointer-coarse:h-11 tablet:-mx-space-2',
                    isLocked ? undefined : 'cursor-pointer',
                  )
                : cn(
                    fieldClass({ invalid: invalid || !!error, locked: isLocked }),
                    'relative flex w-full items-center pr-[var(--field-end-icon)] text-left',
                    appearance === 'cell' ? 'h-8 pl-space-2 text-md pointer-coarse:h-11' : 'h-11 pl-space-4',
                    locked ? undefined : 'select-chevron',
                    isLocked ? undefined : 'cursor-pointer',
                  )
            }
          >
            <span id={valueId} className={cn('min-w-0 truncate', !(isNew && display !== null) && 'flex-1', display === null && (cellLook ? 'text-ink-3' : 'text-ink-2'))}>
              {display ?? placeholder}
            </span>
            {isNew && display !== null ? (
              <span className="flex flex-1 items-center">
                <NewMark />
              </span>
            ) : null}
            {cellLook && !locked ? <Icon icon={ChevronDown} size="sm" aria-hidden className="flex-none text-ink-3 group-hover:text-ink-2" /> : null}
            {locked ? <Icon icon={Lock} size="base" aria-hidden data-testid="feld-schloss" className="absolute right-4 text-ink-2" /> : null}
          </button>
        )}
      >
        {/* Am PC/Tablet so breit wie das Feld (das Popover liegt fest am Bildschirm, damit kein Rand es abschneidet). */}
        <div data-testid="auswahl-liste" className="max-w-[calc(100vw_-_2*var(--space-4))]" style={phone ? undefined : { width: panelWidth }}>
          <input
            ref={input}
            type="text"
            role="combobox"
            aria-label={`${label} suchen`}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={current >= 0 ? optionId(current) : undefined}
            aria-describedby={status ? statusId : undefined}
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="done"
            placeholder={COMBOBOX_TEXTS.search}
            value={query}
            disabled={busy}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
              remote?.onQuery(e.target.value);
            }}
            onKeyDown={onSearchKey}
            className={cn(fieldClass(), 'h-11 w-full px-space-3 max-tablet:text-field-phone')}
          />
          {loadError ? (
            <p id={statusId} role="alert" className="type-meta px-space-3 pt-space-2 pb-space-1 text-clarify">
              {status}
            </p>
          ) : (
            // Live-Region bleibt stehen und wechselt nur den Text, damit Hilfsmittel jede Meldung vorlesen.
            <p id={statusId} role="status" className={cn('type-meta px-space-3 text-ink-2', status && 'pt-space-2 pb-space-1')}>
              {status}
            </p>
          )}
          <ul
            id={listId}
            role="listbox"
            aria-label={label}
            tabIndex={-1}
            className={cn('mt-space-2 flex list-none flex-col overflow-y-auto overscroll-contain p-0', !phone && 'max-h-80')}
          >
            {rows.map((row, i) => (
              <ComboRow
                key={row.kind === 'create' ? 'neu' : row.match.option.value}
                id={optionId(i)}
                row={row}
                createLabel={createLabel}
                similar={offer.kind === 'similar' && i === 0}
                active={i === current}
                current={row.kind === 'option' && row.match.option.value === value}
                separator={i === separatorAt}
                onHover={() => setActive(i)}
                onPick={() => void pick(row, 'field')}
              />
            ))}
          </ul>
          <p role="status" className={cn('type-meta px-space-3 text-ink-3', remote?.more && !loading && !loadError && 'pt-space-1 pb-space-1')}>
            {remote?.more && !loading && !loadError ? COMBOBOX_TEXTS.more : null}
          </p>
        </div>
      </Popover>
      {locked ? (
        <span id={lockId} className="sr-only">
          {lockText(locked)}
        </span>
      ) : null}
      {/* Immer vorhanden, damit ein später eintreffender Fehler (Sofort-Speichern, Rückgängig) sicher angesagt wird. */}
      <div role="alert">
        {error ? (
          <p id={errorId} className="type-meta mt-space-2 text-clarify">
            {error}
          </p>
        ) : null}
      </div>
      {undo.element}
    </div>
  );
}

/** Ein Eintrag: Treffer halbfett, aktueller Wert mit Haken; Klick übernimmt, der Fokus bleibt in der Suchzeile. */
function ComboRow({
  id,
  row,
  createLabel,
  similar,
  active,
  current,
  separator,
  onHover,
  onPick,
}: {
  id: string;
  row: Row;
  createLabel: (text: string) => string;
  /** Vorhandener Eintrag der Frage „Meinst du …?“ (Zusatz „vorhanden“). */
  similar: boolean;
  active: boolean;
  current: boolean;
  separator: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (active) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  return (
    <>
      {separator ? <li role="none" aria-hidden data-testid="auswahl-trenner" className="mx-space-2 my-space-1 border-t border-line" /> : null}
      <li
        ref={ref}
        id={id}
        role="option"
        aria-selected={active}
        data-current={current || undefined}
        onMouseDown={(e) => e.preventDefault()}
        onMouseMove={active ? undefined : onHover}
        onClick={onPick}
        className={cn(
          'flex min-h-10 cursor-pointer items-center gap-space-2 rounded-field px-space-3 py-space-2 text-row text-ink pointer-coarse:min-h-11',
          active && 'bg-tile',
          similar && 'bg-check-bg',
        )}
      >
        {row.kind === 'create' ? (
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere] text-link" data-testid="auswahl-neu-anlegen">
            {row.confirm ? COMBOBOX_TEXTS.createAnywayRow(row.text) : createLabel(row.text)}
          </span>
        ) : (
          <span className="min-w-0 flex-1">
            <span className="block [overflow-wrap:anywhere]">
              {labelSegments(row.match.option.label, row.match.ranges).map((s, i) =>
                s.hit ? (
                  <strong key={i} className="font-semibold">
                    {s.text}
                  </strong>
                ) : (
                  <span key={i}>{s.text}</span>
                ),
              )}
            </span>
            {similar ? (
              <span className="type-meta block text-check" data-testid="auswahl-meinst-du">
                {COMBOBOX_TEXTS.existing}
              </span>
            ) : row.match.option.hint ? <span className="type-meta block text-ink-2">{row.match.option.hint}</span> : null}
          </span>
        )}
        {current ? <Icon icon={Check} size="base" aria-hidden className="flex-none text-ink-2" /> : null}
      </li>
    </>
  );
}

/** Marke „neu“ (Ticket 5.90; DESIGN „Grid“: kleine Pille, neue Werte `ready-fg` auf `ready-bg`). */
export function NewMark() {
  return (
    <span data-testid="marke-neu" className={cn('inline-flex h-pill-h flex-none items-center rounded-pill px-space-2 text-pill font-semibold', pillToneClass('ready'))}>
      {COMBOBOX_TEXTS.newMark}
    </span>
  );
}

